"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  CookieConsent,
  DEFAULT_CONSENT,
  loadCookieConsent,
  saveCookieConsent,
  updateThirdPartyScripts,
  hasGivenConsent,
  isCategoryAllowed,
  type CookieCategory,
  ALL_ACCEPTED_CONSENT,
} from '@/lib/cookie-consent';

interface CookieConsentContextType {
  consent: CookieConsent;
  hasConsent: boolean;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  isCategoryAllowed: (category: CookieCategory) => boolean;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_CONSENT);
  const [hasConsent, setHasConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Inizializzazione lato client
  useEffect(() => {
    setIsClient(true);
    const savedConsent = loadCookieConsent();
    
    if (savedConsent) {
      setConsent(savedConsent);
      setHasConsent(true);
      setShowBanner(false);
      updateThirdPartyScripts(savedConsent);
    } else {
      // Nessun consenso salvato, mostra il banner
      setConsent(DEFAULT_CONSENT);
      setHasConsent(false);
      setShowBanner(true);
    }
  }, []);

  // Listener per aggiornamenti del consenso da altre tab
  useEffect(() => {
    if (!isClient) return;

    const handleConsentUpdate = (event: CustomEvent<CookieConsent | null>) => {
      if (event.detail) {
        setConsent(event.detail);
        setHasConsent(true);
        updateThirdPartyScripts(event.detail);
      } else {
        setConsent(DEFAULT_CONSENT);
        setHasConsent(false);
      }
    };

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate as EventListener);
    
    return () => {
      window.removeEventListener('cookieConsentUpdated', handleConsentUpdate as EventListener);
    };
  }, [isClient]);

  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    const updatedConsent: CookieConsent = {
      ...consent,
      ...newConsent,
      necessary: true, // Sempre true
      timestamp: Date.now(),
    };

    setConsent(updatedConsent);
    setHasConsent(true);
    setShowBanner(false);
    saveCookieConsent(updatedConsent);
    updateThirdPartyScripts(updatedConsent);
  };

  const acceptAll = () => {
    const allAccepted = ALL_ACCEPTED_CONSENT;
    setConsent(allAccepted);
    setHasConsent(true);
    setShowBanner(false);
    setShowSettings(false);
    saveCookieConsent(allAccepted);
    updateThirdPartyScripts(allAccepted);
  };

  const rejectAll = () => {
    const onlyNecessary = DEFAULT_CONSENT;
    setConsent(onlyNecessary);
    setHasConsent(true);
    setShowBanner(false);
    setShowSettings(false);
    saveCookieConsent(onlyNecessary);
    updateThirdPartyScripts(onlyNecessary);
  };

  const checkCategoryAllowed = (category: CookieCategory): boolean => {
    return consent[category] === true;
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsent,
        updateConsent,
        acceptAll,
        rejectAll,
        isCategoryAllowed: checkCategoryAllowed,
        showBanner,
        setShowBanner,
        showSettings,
        setShowSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  
  if (context === undefined) {
    throw new Error('useCookieConsent deve essere usato all\'interno di CookieConsentProvider');
  }
  
  return context;
}
